import { PastScheduleWarning } from './PastScheduleWarning.tsx';
import React, { useState } from 'react';
import type { LaunchTemplateListItem } from './types.ts';

// Convert ISO string to datetime-local value using user's chosen timezone
function toDatetimeLocal(iso: string | null, tzOffsetHours: number): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const offset = tzOffsetHours * 60; // in minutes
    const local = new Date(d.getTime() + offset * 60000);
    return local.toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

// Get ISO string from datetime-local value using user's chosen timezone
function fromDatetimeLocal(val: string, tzOffsetHours: number): string {
  // val is like "2026-08-28T08:00"
  // Treat this string as UTC, then subtract the offset to get the real UTC
  const d = new Date(val + 'Z'); 
  const utcMs = d.getTime() - (tzOffsetHours * 3600000);
  return new Date(utcMs).toISOString();
}

// Format UTC offset e.g. "+03:00"
function formatTzOffset(hours: number): string {
  const sign = hours >= 0 ? '+' : '-';
  const abs = Math.abs(hours);
  const h = String(Math.floor(abs)).padStart(2, '0');
  const m = String(Math.round((abs % 1) * 60)).padStart(2, '0');
  return `${sign}${h}:${m}`;
}

export function LaunchSettingsStep({
  templates,
  templateId,
  setTemplateId,
  pauseSeconds,
  setPauseSeconds,
  scheduledAt,
  setScheduledAt,
  tzOffsetHours,
  setTzOffsetHours,
}: {
  templates: LaunchTemplateListItem[];
  templateId: number | null;
  setTemplateId: (id: number | null) => Promise<void>;
  pauseSeconds: number;
  setPauseSeconds: (n: number) => void;
  scheduledAt: string | null;
  setScheduledAt: (value: string | null) => void;
  tzOffsetHours: number;
  setTzOffsetHours: (value: number) => void;
}) {
  const [scheduled, setScheduled] = useState(Boolean(scheduledAt));

  return (
    <div className="wizard-step">
      <label className="field">
        <span className="label">Template</span>
        <select
          className="input"
          value={templateId ?? ''}
          onChange={(e) => { void setTemplateId(e.target.value ? Number(e.target.value) : null); }}
        >
          <option value="">Select template</option>
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </label>

      <div className="checks">
        <label className="toggle">
          <input
            type="radio"
            checked={!scheduled}
            onChange={() => { setScheduled(false); setScheduledAt(null); }}
          /> Start now
        </label>
        <label className="toggle">
          <input
            type="radio"
            checked={scheduled}
            onChange={() => setScheduled(true)}
          /> Schedule ({formatTzOffset(tzOffsetHours)})
        </label>
      </div>

      {scheduled && (
        <label className="field">
          <span className="label">Start date and time (Timezone: {formatTzOffset(tzOffsetHours)})</span>
          <input
            className="input"
            type="datetime-local"
            value={toDatetimeLocal(scheduledAt, tzOffsetHours)}
            onChange={(e) => setScheduledAt(e.target.value ? fromDatetimeLocal(e.target.value, tzOffsetHours) : null)}
          />
        </label>
      )}

      <PastScheduleWarning dates={[scheduledAt]} />
      <label className="field">
        <span className="label">Your Timezone (saved automatically)</span>
        <select
          className="input"
          value={tzOffsetHours}
          onChange={(e) => setTzOffsetHours(Number(e.target.value))}
        >
          <option value={-12}>UTC-12:00 (Baker Island)</option>
          <option value={-11}>UTC-11:00 (Samoa)</option>
          <option value={-10}>UTC-10:00 (Hawaii)</option>
          <option value={-9}>UTC-09:00 (Alaska)</option>
          <option value={-8}>UTC-08:00 (Pacific Time)</option>
          <option value={-7}>UTC-07:00 (Mountain Time)</option>
          <option value={-6}>UTC-06:00 (Central Time)</option>
          <option value={-5}>UTC-05:00 (Eastern Time)</option>
          <option value={-4}>UTC-04:00 (Atlantic Time)</option>
          <option value={-3}>UTC-03:00 (Buenos Aires)</option>
          <option value={-2}>UTC-02:00 (Mid-Atlantic)</option>
          <option value={-1}>UTC-01:00 (Azores)</option>
          <option value={0}>UTC±00:00 (London, Lisbon)</option>
          <option value={1}>UTC+01:00 (Berlin, Paris)</option>
          <option value={2}>UTC+02:00 (Athens, Cairo)</option>
          <option value={3}>UTC+03:00 (Kyiv, Istanbul, Moscow)</option>
          <option value={4}>UTC+04:00 (Dubai, Baku)</option>
          <option value={5}>UTC+05:00 (Islamabad, Karachi)</option>
          <option value={6}>UTC+06:00 (Dhaka, Almaty)</option>
          <option value={7}>UTC+07:00 (Bangkok, Jakarta)</option>
          <option value={8}>UTC+08:00 (Beijing, Singapore)</option>
          <option value={9}>UTC+09:00 (Tokyo, Seoul)</option>
          <option value={10}>UTC+10:00 (Sydney, Guam)</option>
          <option value={11}>UTC+11:00 (Solomon Is.)</option>
          <option value={12}>UTC+12:00 (Auckland, Fiji)</option>
          <option value={13}>UTC+13:00 (Tonga)</option>
          <option value={14}>UTC+14:00 (Kiribati)</option>
        </select>
      </label>

      <label className="field">
        <span className="label">Pause between accounts (seconds)</span>
        <input
          className="input"
          min="0"
          type="number"
          value={pauseSeconds}
          onChange={(e) => setPauseSeconds(Number(e.target.value))}
        />
      </label>
    </div>
  );
}
